import { useMemo, useState } from "react";
import { IconButton } from "@/components/common/icon-button";
import { useCradSize } from "@/hooks/use-card-size";
import { Tooltip } from "@/components/ui/modal";
import { useI18nNavigate } from "@/hooks/use-i18n-navigate";
import { MaterialCard } from "./material-card";
import { MaterialCreateCard } from "./material-create-crad";
import { MaterialSkeletonList } from "./material-card-skeleton";
import { cn } from "@/lib/utils";
import { MaterialEmptyImage, PlusCircleIcon } from "./assets";
import { useTranslation } from "react-i18next";
import { AssetsToolBar } from "../common/assets-tool-bar";
import { assetsMaterialHooks } from "@/manager";
import { LoadingOverlay } from "../common/loading-overlay";
import { TextButton } from "../common/text-button";
import { PlusIcon } from "lucide-react";
import { getNextName } from "@/utils/get-next-name";
import type { UserMaterialFolderData } from "@/services/user-material-list-folders";

// 按接口顺序去重 type，再映射为展示用标签文案
function folderTagLabels(
  relatedAgents: UserMaterialFolderData["relatedAgents"],
  tagTypeMap: Record<number, string>,
): string[] {
  const types = [...new Set(relatedAgents?.map((a) => a.type) ?? [])];
  return types.map((type) => tagTypeMap[type]).filter(label => Boolean(label));
}

/** 资产-素材库 */
export const AssetsMaterial = () => {
  const { t } = useTranslation();
  const navigate = useI18nNavigate();
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isCreate, setIsCreate] = useState(false);

  // 资产素材库目录列表数据
  const { materialFolders, loading } = assetsMaterialHooks.useMaterialFoldersData();

  const { createMaterial, loading: createLoading } = assetsMaterialHooks.useMaterialFolderCreate();
  const { renameMaterial } = assetsMaterialHooks.useMaterialFolderRename();
  const { deleteMaterial, loading: deleteLoading } = assetsMaterialHooks.useMaterialFolderDelete();
  const { preparing, downloadMaterialFolder } = assetsMaterialHooks.useDownloadMaterialFolder();

  const { ref, columns } = useCradSize({
    min: 170,
    max: 220,
    gap: 5,
  });

  // 素材类型标签文案映射
  const tagTypeMap: Record<number, string> = useMemo(
    () => ({
      1: t("agent_creation.video"),
      6: t("agent_creation.ai_creation"),
      4: t("agent_creation.post"),
      5: t("agent_creation.article"),
      2: t("agent_creation.podcast"),
      3: t("agent_creation.moments"),
    }),
    [t],
  );

  // 处理选中单个文件夹
  const handleSelected = ({ id, checked }: { id: number; checked: boolean }) => {
    setSelectedIds(prev => {
      const nextSet = new Set(prev);
      if (checked) {
        nextSet.add(id);
      } else {
        nextSet.delete(id);
      }
      return Array.from(nextSet);
    });
  }

  // 处理全选/反选所有文件夹
  const handleSelectedAll = (checked: boolean) => {
    setSelectedIds(prev => {
      if (checked) {
        return materialFolders.map(item => item.id);
      }
      return [];
    });
  }

  // 处理取消选择/退出多选模式
  const handleCancelSelect = () => {
    setSelectedIds([]);
  }

  // 批量删除
  const handleBatchDelete = async () => {
    setIsCreate(false)
    deleteMaterial(selectedIds);
    handleCancelSelect();
  }

  // 单个删除
  const handleSingleDelete = async (id: number) => {
    setIsCreate(false)
    deleteMaterial(id);
  }

  // 处理下载选中文件夹
  const handleSingleDownload = async (id?: number) => {
    handleCancelSelect();
    await downloadMaterialFolder([id]);
  }

  const handlePatchDownload = async () => {
    handleCancelSelect();
    await downloadMaterialFolder(selectedIds);
  }

  // 跳转对应素材页面
  const handleDetail = (id: number) => {
    navigate(`/assets/material/${id}/`);
  }

  // 处理重命名
  const handleRename = (id: number, newName: string) => {
    renameMaterial({ id, name: newName });
    setIsCreate(false)
  }

  // 处理创建素材（先采用先创建，后命名的方式，如果有问题再考虑先命名再创建）
  const handleCreate = async () => {
    try {
      const nextName = getNextName(materialFolders, t('assets.material.untitled'));
      await createMaterial({ name: nextName })
      setTimeout(() => {
        setIsCreate(true);
      }, 100);
    } catch (error) {
      console.log(error)
    }
  }

  // 处理容器点击（空白处点击，如果有出现点击其他地方关闭了工具栏，考虑是否是这里的问题）
  const handleContainerClick = (e: React.MouseEvent) => {
    // 如果当前没有选中的项，或者点击的是工具栏等 UI，不触发
    if (selectedIds.length > 0) {
      handleCancelSelect();
    }
  };

  // 处理标签
  const folderTagsById = useMemo(() =>
    new Map(
      materialFolders.map((item) => [
        item.id,
        folderTagLabels(item.relatedAgents, tagTypeMap),
      ]),
    ),
    [materialFolders, tagTypeMap],
  );

  return (
    <div className="size-full" onClick={handleContainerClick}>
      <div className="relative h-full">
        <div className="flex flex-col relative p-8 pt-3 h-full overflow-hidden">
          <header className="flex justify-between items-center mb-[18px] z-[50]">
            <div className="text-[20px] font-semibold text-[rgba(16,24,40,1)]">{t('assets.material.title')}</div>
            <div>
              <Tooltip
                side="top"
                align="center"
                trigger={
                  <IconButton
                    className="size-8"
                    onClick={handleCreate}
                    icon={<PlusCircleIcon className="size-6" />}
                  />
                }
                content={
                  <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px]'>
                    <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
                      {t('assets.material.create_folder')}
                    </span>
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-b-[6px] border-l-transparent border-r-transparent border-b-black"></div>
                  </div>
                }
              />
            </div>
          </header>
          <div className="h-full overflow-y-auto pt-2">
            <div
              ref={ref}
              className='grid gap-5 gap-y-24 mb-20'
              style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
            >
              {/* <MaterialCreateCard onCreate={handleCreate} loading={createLoading} /> */}
              {loading && <MaterialSkeletonList count={15} />}
              {!loading && materialFolders.length > 0 && materialFolders.map((item, index) => (
                <MaterialCard
                  key={item.id}
                  id={item.id}
                  name={item.folderName}
                  time={item.createTime}
                  cover={item.showPic}
                  tags={folderTagsById.get(item.id)}
                  isSelected={selectedIds.includes(item.id)}
                  openSelected={selectedIds.length > 0}
                  // 第一个是新创建的素材目录
                  isEditName={index === 0 && isCreate && !createLoading}
                  onSelected={handleSelected}
                  onDelete={handleSingleDelete}
                  onDownload={handleSingleDownload}
                  onRename={handleRename}
                  onDetail={handleDetail}
                />
              ))}
            </div>
            {!loading && !materialFolders.length && (
              <div className="absolute inset-0  flex items-center justify-center">
                <div className="flex flex-col items-center">
                  <MaterialEmptyImage className="w-[211px] mb-3" />
                  <span className="text-[rgba(102,112,133,1)] text-sm mb-4">
                    {t('assets.material.empty_tips')}
                  </span>
                  <TextButton
                    icon={<PlusIcon className="size-5" />}
                    onClick={handleCreate}
                    label={t('assets.material.create_folder')}
                    className="text-sm font-medium"
                    loading={createLoading}
                    disabled={createLoading}
                  />
                </div>
              </div>
            )}
          </div>
          <div
            className={cn(
              "transition-all duration-300 ease-in-out",
              selectedIds.length > 0 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6 pointer-events-none"
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <AssetsToolBar
              onClose={handleCancelSelect}
              isSelectedAll={selectedIds.length === materialFolders.length}
              selectedAmount={selectedIds.length}
              onSelectedAll={handleSelectedAll}
              onDelete={handleBatchDelete}
              onDownload={handlePatchDownload}
              deleteLoading={deleteLoading}
            />
          </div>
        </div>
        <LoadingOverlay loading={deleteLoading || createLoading} />
      </div>
      <LoadingOverlay loading={preparing} text={t('assets.material.preparing_download')} />
    </div>
  )
}